import { memo } from "react";
import { ChevronRight, Filter, FilterX, Flag } from "lucide-react";
import { TableRow, TableCell } from "@/components/ui/table";
import { MARKS } from "@/config/filterFields";
import { cn } from "@/lib/utils";
import type { ClicksMark } from "@/services/ClicksService";
import type { CampaignStat } from "@/types/clicks";
import { CopyableValue } from "./CopyableValue";
import { MarkIcon } from "./MarkMenu";
import { MetricCell } from "./MetricCell";
import {
  CLICKS_CELL_CLASS,
  attributeOf,
  formatAttribute,
  isNumericAttribute,
  metricLabel,
  type GridColumn,
  type MetricFieldId,
} from "./metricDisplay";

interface ClicksTableRowProps {
  row: CampaignStat;
  prevRow?: CampaignStat;
  /** The middle columns (brand, campaign_group, metrics) in display order. */
  columns: GridColumn[];
  /** Header of the first column, used as the label in the context menu. */
  dimensionLabel?: string;
  /** Only domain rows break down further — there is nothing under a country. */
  expandable?: boolean;
  /** Domains and URLs copy themselves when clicked; a country has nothing to copy. */
  copyable?: boolean;
  isOpen?: boolean;
  onToggle?: () => void;
  /** Checkbox to overlay this row on the trend chart (compare mode). */
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: () => void;
  /** Right-click on a cell. The row knows what each cell holds; the table owns the menu. */
  onCellContextMenu?: (
    event: React.MouseEvent,
    cell: { value: string; columnLabel: string; kind: "dimension" | "brand" | "campaign_group" | "attr" | "metric" },
  ) => void;
  /** Cells appended after the metrics (keyword volume). */
  trailing?: React.ReactNode;
  /**
   * "Only this" / "Exclude this" buttons by the value - the right-click menu's
   * "Filter by this value" / "Exclude this value", in sight (owner, 30.10.2026).
   */
  onQuickFilter?: (value: string, mode: "include" | "exclude") => void;
  /** The value's shared mark (20261030183128); its button opens the mark menu. */
  mark?: ClicksMark | null;
  onMarkClick?: (event: React.MouseEvent, value: string) => void;
}

// Shown on hover and on keyboard focus anywhere in the row; always on touch screens.
const ON_ROW_HOVER =
  "opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 [@media(pointer:coarse)]:opacity-100";
const markLabel = (mark: string) => MARKS.find((m) => m.value === mark)?.label ?? mark;

export const ClicksTableRow = memo(function ClicksTableRow({
  row,
  prevRow,
  columns,
  dimensionLabel = "Value",
  expandable = false,
  copyable = false,
  isOpen = false,
  onToggle,
  selectable = false,
  selected = false,
  onToggleSelect,
  onCellContextMenu,
  trailing,
  onQuickFilter,
  mark,
  onMarkClick,
}: ClicksTableRowProps) {
  const value = row.dimension_value;
  return (
    <TableRow
      className={cn(
        "group border-b border-border transition-colors last:border-0 hover:bg-muted/40",
        expandable && "cursor-pointer",
        isOpen && "bg-muted/30",
      )}
      onClick={expandable ? onToggle : undefined}
    >
      <TableCell
        className={cn(
          CLICKS_CELL_CLASS,
          // max-w, not w: the column takes only the width it needs, so a table
          // of short domains stays narrow and a long one grows up to the cap.
          // 600px covers the longest real domain measured (80 characters);
          // past that the value is not a domain at all.
          "font-medium max-w-[600px]",
          // Frozen first column: stays put on horizontal scroll (solid bg so the
          // scrolling columns don't bleed through).
          "sticky left-0 z-10 bg-card",
        )}
        title={row.dimension_value}
        onContextMenu={(event) =>
          onCellContextMenu?.(event, {
            value: row.dimension_value,
            columnLabel: dimensionLabel,
            kind: "dimension",
          })
        }
      >
        {/* The chevron and the value are siblings, never nested. The value is
            itself a <button> when it can be copied, and a button inside a
            button is invalid HTML — the browser un-nests it and the label
            disappears, which is exactly what happened on 28.09.2026. */}
        <div className="flex items-center gap-1.5">
          {selectable ? (
            <input
              type="checkbox"
              checked={selected}
              onChange={onToggleSelect}
              onClick={(event) => event.stopPropagation()}
              title="Overlay this row on the trend chart"
              className="size-3.5 shrink-0 cursor-pointer accent-primary"
            />
          ) : null}
          {expandable ? (
            <button
              type="button"
              aria-expanded={isOpen}
              aria-label={isOpen ? "Collapse pages" : "Show pages"}
              onClick={(event) => {
                event.stopPropagation();
                onToggle?.();
              }}
              className="flex-shrink-0 cursor-pointer"
            >
              <ChevronRight
                className={cn(
                  "size-4 text-muted-foreground transition-transform",
                  isOpen && "rotate-90",
                )}
                aria-hidden
              />
            </button>
          ) : null}

          {onMarkClick ? (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onMarkClick(event, value);
              }}
              aria-label={mark ? `${markLabel(mark.mark)} - change the mark of ${value}` : `Mark ${value}`}
              title={mark ? `${markLabel(mark.mark)}${mark.by ? ` · ${mark.by}` : ""}` : "Mark: suspicious, watch, pause"}
              className={cn(
                "inline-flex size-6 shrink-0 cursor-pointer items-center justify-center rounded hover:bg-muted",
                !mark && ON_ROW_HOVER,
              )}
            >
              {mark ? <MarkIcon mark={mark.mark} /> : <Flag className="size-3.5 text-muted-foreground" aria-hidden />}
            </button>
          ) : null}

          {copyable ? (
            <CopyableValue
              value={row.dimension_value}
              placeholder="Unknown"
              className="text-foreground"
            />
          ) : (
            <span className="truncate text-foreground">
              {row.dimension_value || (
                <span className="italic text-muted-foreground/70">Unknown</span>
              )}
            </span>
          )}

          {/* An empty value has nothing to filter by (and no name to say). */}
          {onQuickFilter && value ? (
            <span className={cn("ml-auto flex shrink-0 items-center gap-0.5", ON_ROW_HOVER)}>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onQuickFilter(value, "include");
                }}
                aria-label={`Only ${value}`}
                title="Only this value"
                className="inline-flex size-6 cursor-pointer items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <Filter className="size-3.5" aria-hidden />
              </button>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onQuickFilter(value, "exclude");
                }}
                aria-label={`Exclude ${value}`}
                title="Exclude this value"
                className="inline-flex size-6 cursor-pointer items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-destructive dark:hover:text-red-400"
              >
                <FilterX className="size-3.5" aria-hidden />
              </button>
            </span>
          ) : null}
        </div>
      </TableCell>

      {columns.map((col) => {
        if (col.kind === "brand")
          return (
            <AttributeCell
              key="brand"
              value={row.dimension_brand}
              // 290px fits the longest brand in the data (38 characters), so the
              // ellipsis never actually fires — the cap is only a guard.
              maxWidth="max-w-[290px]"
              onContextMenu={(event) =>
                onCellContextMenu?.(event, {
                  value: row.dimension_brand ?? "Not set",
                  columnLabel: "Brand",
                  kind: "brand",
                })
              }
            />
          );
        if (col.kind === "campaign_group")
          return (
            <AttributeCell
              key="campaign_group"
              value={row.dimension_campaign_group}
              maxWidth="max-w-[220px]"
              onContextMenu={(event) =>
                onCellContextMenu?.(event, {
                  value: row.dimension_campaign_group ?? "Not set",
                  columnLabel: "Campaign Group",
                  kind: "campaign_group",
                })
              }
            />
          );
        if (col.kind === "attr") {
          // A field of the row's domain (Domain Tracker), merged onto the row on the page.
          const shown = formatAttribute(col.id, attributeOf(row, col.id));
          return (
            <AttributeCell
              key={col.id}
              value={shown}
              numeric={isNumericAttribute(col.id)}
              maxWidth="max-w-[220px]"
              onContextMenu={(event) =>
                onCellContextMenu?.(event, { value: shown ?? "Not set", columnLabel: metricLabel(col.id), kind: "attr" })
              }
            />
          );
        }
        const metric = col.metric;
        return (
          <MetricCell
            key={metric.id}
            metricId={metric.id as MetricFieldId}
            row={row}
            prevRow={prevRow}
            cellClass={CLICKS_CELL_CLASS}
            onContextMenu={(event, text) =>
              onCellContextMenu?.(event, {
                value: text,
                columnLabel: metricLabel(metric.id, metric.label),
                kind: "metric",
              })
            }
          />
        );
      })}
      {trailing}
    </TableRow>
  );
});

function AttributeCell({
  value,
  maxWidth,
  numeric = false,
  onContextMenu,
}: {
  value: string | null | undefined;
  maxWidth: string;
  /** A count or an amount: right-aligned like the metrics. */
  numeric?: boolean;
  onContextMenu: (event: React.MouseEvent) => void;
}) {
  return (
    <TableCell
      className={cn(CLICKS_CELL_CLASS, maxWidth, numeric && "text-right tabular-nums")}
      title={value ?? undefined}
      onContextMenu={onContextMenu}
    >
      <div className="truncate text-muted-foreground">
        {value ?? <span className="italic text-muted-foreground/70">Not set</span>}
      </div>
    </TableCell>
  );
}
